import './DocViewer.scss'
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from '../../../../services/appInsights.service';
import { useEffect, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Authorize, UserAuthContext } from '../../appLoginComponent/UserAuthContext';
import NotAuthorized from '../../appLoginComponent/NotAuthorized';
import { Document } from '../../../../interfaces/tester.interface';
import { AttachmentGetRequest } from '../../../../services/AttachmentGetRequest';
import Spinner from '../../../components/Spinner';
import { isAndroidMobile } from '../../../../helpers/Utilities';

function DocViewer() {
    let { isAuthorized } = useContext(
        UserAuthContext
    ) as Authorize;

    const location = useLocation();
    const { documentLabel, document } = location.state || {};
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [openedInNewTab, setOpenedInNewTab] = useState(false);

    useEffect(() => {
        sessionStorage.setItem('returnFromIframe', 'true');

        // Get or set document label
        const labelToStore = documentLabel || sessionStorage.getItem('docViewerLabel') || '';
        sessionStorage.setItem('docViewerLabel', labelToStore);

        // Store document metadata if provided
        if (document) {
            sessionStorage.setItem('currentDocument', JSON.stringify(document));
        }

        // Always try to load from sessionStorage (handles both initial load and refresh)
        const storedDoc = sessionStorage.getItem('currentDocument');
        if (storedDoc) {
            try {
                const parsedDoc = JSON.parse(storedDoc);
                loadPdfInIframe(parsedDoc);
            } catch (err) {
                console.error('Failed to parse stored document:', err);
                setError('Unable to load document. Please go back and try again.');
                setIsLoading(false);
            }
        } else {
            setError('No document information found. Please go back and select a document.');
            setIsLoading(false);
        }

        // Cleanup blob URL on unmount
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, []);

    const ensurePdfMimeType = (blob: Blob): Blob => {
        if (blob.type && blob.type !== 'application/octet-stream') {
            return blob;
        }
        return new Blob([blob], { type: 'application/pdf' });
    };


    const loadPdfInIframe = async (doc: Document) => {
        setIsLoading(true);
        setError('');
        setOpenedInNewTab(false);
        try {
            const request = new AttachmentGetRequest(doc);
            const blob = await request.send();

            if (!(blob instanceof Blob)) {
                throw new TypeError('Download failed: response is not a Blob');
            }

            const typedBlob = ensurePdfMimeType(blob);
            const blobUrl = URL.createObjectURL(typedBlob);

            if (isAndroidMobile()) {
                window.open(blobUrl, '_blank', 'noopener,noreferrer');
                setOpenedInNewTab(true);
                setPdfUrl(blobUrl);
                setIsLoading(false);
                return;
            }

            setPdfUrl(blobUrl);
            setIsLoading(false);
        } catch (err) {
            console.error('Failed to load PDF:', err);
            setError('Failed to load document. Please try again.');
            setIsLoading(false);
        }
    };

    return (
        <>
            {isAuthorized("AV_Vehicle_Search") ? (
                <div className="doc-viewer-container">
                    {isLoading && <Spinner />}
                    {error && (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                            {error}
                        </div>
                    )}
                    {!isLoading && !error && openedInNewTab && (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            Document opened in a new tab.
                        </div>
                    )}
                    {!isLoading && !error && pdfUrl && !openedInNewTab && (
                        <iframe
                            src={pdfUrl}
                            className="frame-viewer"
                            title="Document Viewer"
                            frameBorder="0"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                        />
                    )}
                </div>
            ) : (
                <NotAuthorized />
            )}
        </>
    )
}

export default withAITracking(reactPlugin, DocViewer);